<template lang="pug">
  .bumper-customizer
    terms-and-conditions-modal(v-if="showTermsAndConditions" @accept="acceptTerms" @close="showTermsAndConditions = false")
    Tabs(:tabs="['Add Text', 'Upload Logo', 'Add Rogue Design']" :modelValue="tab" @update:modelValue="switchTab($event)")
    .file-upload(v-if="mounted && modelValue.type === 'file'")
      .uploads(v-if="fileOpts.length > 0")
        b Current & Recent Uploads
        Swatches.img-choice(
          :options="fileOpts"
          :modelValue="uploads.indexOf(modelValue.image)"
          @update:modelValue="setProp('image', uploads[$event])"
        )
      .drop-area
        .disclaimer(v-if="fileOpts.length === 0")
          i Accepted image file types are .jpg and .png with a max size of 10MB. For best results, use a landscape orientation image.
          label.terms
            input(type="checkbox" v-model="termsChecked")
            span I agree to the &nbsp;
            span.link(@click.stop.prevent="showTermsAndConditions = true") Terms and Conditions
        DragAndDropFileUpload(
          @fileUpload="uploadFile"
          linkText="Upload an Image"
          :loading="loading"
          icon="misc/camera-plus"
          buttonText="Upload New Image"
          :disabled="!termsChecked && fileOpts.length === 0"
          :error="error"
          :showSelected="true"
          :showButton="false"
          )
    .text(v-else-if="modelValue.type === 'text'")
      RogueInput(label="Add Text" :modelValue="modelValue.text" @update:modelValue="setProp('text', $event)" :maxlength="26")
      p.small {{maxCharactersLeft}} characters remaining
      Swatches.font-choice(
        :options="fontOpts"
        :modelValue="modelValue.font"
        @update:modelValue="setProp('font', $event)"
        label='Select Font')
    .design(v-else)
      Swatches.design-choice(
        label='Select Design'
        :options="designOpts"
        :modelValue="(designOpts.find((d) => d.svg === modelValue.design) || {}).value"
        @update:modelValue="updateDesign($event)")
    Swatches(
      :options="colorOpts"
      :modelValue="modelValue.color"
      @update:modelValue="setProp('color', $event)"
      label='Change Color'
      :colors="swatchColors")
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { CUSTOM_BUMPER_DATA, PRINTER_COLORS_HEX, SWATCH_COLORS } from './constants'
import FONTS from './custom-fonts'
import ROGUE_DESIGNS from './rogue-designs'
import type { CustomBumperDesign } from './types'
import { buildFileOptions, cacheFileOption, isImage, isSVG, lightOrDark } from './utils'

const TermsAndConditionsModal = defineAsyncComponent(() => import('./terms-and-conditions-modal.vue'))

const MAX_FILE_SIZE_MB = 10
const TRANSPARENCY_PERCENTAGE_THRESHOLD = 0.4

const props = defineProps<{
  modelValue: CustomBumperDesign,
  sku: string,
}>()

const emit = defineEmits<{
  (e: 'toggle-rogue-logo', isRogue: boolean): void,
  (e: 'update:modelValue', config: CustomBumperDesign): void,
}>()

const tabMap: { [label: string]: string } = { 'Add Text': 'text', 'Upload Logo': 'file', 'Add Rogue Design': 'design' }

const fontOpts = [
  { label: 'Arvo', svg: FONTS.ARVO, value: 'arvo' },
  { label: 'Arimo', svg: FONTS.ARIMO, value: 'arimo' },
  { label: 'Montserrat', svg: FONTS.MONTSERRAT, value: 'montserrat' },
]

const designOpts = [
  { value: 0, svg: ROGUE_DESIGNS.ROGUE, label: 'Rogue' },
  { value: 1, svg: ROGUE_DESIGNS.RBB, label: 'Rogue Barbell Club' },
  { value: 2, svg: ROGUE_DESIGNS.FLAG, label: 'Flag' },
  { value: 3, svg: ROGUE_DESIGNS.INTERNATIONAL, label: 'International' },
  { value: 4, svg: ROGUE_DESIGNS.DONT_WEAKEN, label: 'Don\'t Weaken' },
]

const fileOpts = ref<{ image: string, value: number }[]>([])
const uploads = ref<string[]>([])
const loading = ref<boolean>(false)
const termsChecked = ref<boolean>(false)
const error = ref<string | null>(null)
const showTermsAndConditions = ref<boolean>(false)
const selectedDesign = ref<number | null>(null)
const mounted = ref<boolean>(false)

const colorOpts = computed(() => CUSTOM_BUMPER_DATA[props.sku].colorOptions.map(({ label, value }) => ({
  imgAttr: value,
  value,
  label,
})))

const maxCharactersLeft = computed(() => -(props.modelValue.text.length - 26))

const tab = computed(() => Object.keys(tabMap).filter(key => tabMap[key] === props.modelValue.type)[0])

const swatchColors = computed(() => (props.sku !== 'IP0535-CST-Group' ? SWATCH_COLORS : PRINTER_COLORS_HEX))

// Shortcut to emit a new value (like v-model). More "correct" to do this than manipulate the object directly
// although that probably would have worked
function setProp(key: keyof CustomBumperDesign, val: unknown) {
  emit('update:modelValue', Object.assign({}, props.modelValue, { [key]: val }))
}

function updateDesign(index: number) {
  const design = designOpts[index]
  selectedDesign.value = index
  emit('toggle-rogue-logo', design.label === 'Rogue')
  setProp('design', design.svg)
}

function switchTab(tabLabel: string) {
  const selected = tabMap[tabLabel]
  if (selected !== 'design') {
    emit('toggle-rogue-logo', false)
    setProp('design', null)
  } else if (selectedDesign.value === 0) {
    emit('toggle-rogue-logo', true)
  }
  setProp('type', selected)
}

function validate(file: File, imageUrl: string) {
  // @todo what if they upload an SVG? We will need to validate it
  if ((file.size / 1024 / 1024) > MAX_FILE_SIZE_MB) {
    error.value = 'File exceeds the max limit'

    return false
  }
  if (isSVG(file)) {
    return true
  }
  if (!isImage(file.name)) {
    error.value = 'File is not an image'

    return false
  }

  return !fileOpts.value.find((option) => option.image === imageUrl)
}

async function createTracedImage(file: File): Promise<string> {
  // don't trace the image if already and svg
  if (isSVG(file)) {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = () => {
        let svg = reader.result as string
        const template = document.createElement('template')
        svg = svg.replace(/fill:(#[A-Fa-f0-9]+)/g, (result, value) => {
          if (value === 'none') {
            return 'none'
          }
          const brightness = lightOrDark(value)

          return `fill-opacity:"${brightness === 'dark' ? 1 : 0}"`
        })
        template.innerHTML = svg
        const svgChild = template.content.querySelector('svg')!
        svgChild.setAttribute('height', '500')
        svgChild.setAttribute('width', '500')
        svgChild.setAttribute('class', '')
        resolve(svgChild.outerHTML)
      }
      reader.readAsText(file)
    })
  }
  // Tell webpack not to chunk the svg-png-convert in with the main Vendor chunk, since it is rarely used
  const { png2svg } = await import(/* webpackChunkName: "svgtopng" */'@rogue/svg-png-converter')
  // convert image to svg (actually accepts any image file, not just pngs)
  const img = await png2svg({ input: file, tracer: 'potrace', numberofcolors: 1 })

  // Printer doesn't allow for transparency, so replace all opacities with either 1 or 0 depending on percentage
  return img.content.replace(/fill-opacity="(.*?)"/g, (_result: string, value: string) => {
    const updatedValue = +value < TRANSPARENCY_PERCENTAGE_THRESHOLD ? 0 : 1

    return `fill-opacity="${updatedValue}"`
  })
}

async function uploadFile(file: File) {
  // first create a local url for the file just uploaded
  const imageUrl = URL.createObjectURL(file)
  if (!validate(file, imageUrl)) {
    return
  }
  loading.value = true
  // @todo Need error handling in this functionx
  // add the image the user uploaded to existing options
  fileOpts.value.push({ image: imageUrl, value: fileOpts.value.length })
  const tracedImage = await createTracedImage(file)
  // Upload the current image to the rendering
  setProp('image', tracedImage)
  // save local image blob, if browser cache is cleared this will be gone, but that's probably fine
  cacheFileOption(file, tracedImage)
  uploads.value.push(tracedImage)
  loading.value = false
}

function acceptTerms() {
  termsChecked.value = true
  showTermsAndConditions.value = false
}

onMounted(() => {
  buildFileOptions().forEach(({ file, trace }, i) => {
    fileOpts.value.push({ image: URL.createObjectURL(file), value: i })
    uploads.value.push(trace as string)
  })
  mounted.value = true
})
</script>
<style lang="scss" scoped>
@use "custom-fonts.css";
.uploads {
  b {
    display: block;
    margin-bottom: $size-xxs;
  }
}
.disclaimer {
  margin-bottom: $size-s;
  display: block;
  font-size: 12px;
  line-height: 18px;
  flex-direction: column;
  align-items: flex-start;
  .terms {
    display: flex;
    align-items: center;
    margin-top: $size-xxs;
  }
}
.small {
  font-size: $size-xxs;
}
.drop-area {
  margin-bottom: $size-s;
}
.font-choice :deep() {
  .swatch[data-value="arvo"]{
    font-family: 'Arvo', serif;
    font-weight: 700;
  }
  .swatch[data-value="arimo"]{
    font-family: 'Arimo', sans-serif;
    font-weight: 700;
  }
  .swatch[data-value="montserrat"]{
    font-family: 'Montserrat', sans-serif;
    font-weight: 800;
  }
}

</style>
